import { CrmTaskStatus } from '@plato/data/prisma';
import {
    Card,
    CardComplete,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardOpenIcon,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import { PlusIcon, UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../../../components/accordion/accordion.tsx';
import { Button } from '../../../components/ui/button.tsx';
import Text from '../../../components/ui/text.tsx';
import { formatMonthShortDay } from '../../../lib/date-helpers.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { plainTextToLexicalSync } from '../../../lib/rich-text/plain-text-to-lexical.ts';
import type { CrmTaskInputDTO, CrmTaskIntent } from '../../../server/dto/crm-task-input-dto.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import type { TaskBulkFormValues } from '../../tasks/components/task-bulk-form-dialog.tsx';
import { TaskBulkFormDialog } from '../../tasks/components/task-bulk-form-dialog.tsx';
import { TaskFormDialog } from '../../tasks/components/task-form-dialog.tsx';

type TaskDialogProps = {
    title: string;
    initialValues: Partial<CrmTaskInputDTO>;
    onSubmit: (task: CrmTaskInputDTO) => void | Promise<void>;
};

const TaskDialog = DialogStateManager.create<TaskDialogProps>(
    ({ title, initialValues, onSubmit }: TaskDialogProps) => {
        const { visible, remove } = useDialog();

        return (
            <TaskFormDialog
                open={visible}
                onClose={remove}
                onSubmit={onSubmit}
                title={title}
                initialValues={initialValues}
            />
        );
    },
);

type TaskBulkDialogProps = {
    title: string;
    initialValues: Partial<CrmTaskInputDTO>;
    onSubmit: (tasks: CrmTaskInputDTO[]) => void;
};

const TaskBulkDialog = DialogStateManager.create<TaskBulkDialogProps>(
    ({ title, initialValues, onSubmit }: TaskBulkDialogProps) => {
        const { visible, remove } = useDialog();
        const { assignedToId, ...shared } = initialValues;

        const bulkInitialValues: Partial<TaskBulkFormValues> = assignedToId
            ? { ...shared, assignedToIds: [assignedToId] }
            : shared;

        return (
            <TaskBulkFormDialog
                open={visible}
                onClose={remove}
                onSubmit={({ assignedToIds, ...values }) => {
                    onSubmit(assignedToIds.map((id) => ({ ...values, assignedToId: id })));
                    remove();
                }}
                isSubmitting={false}
                title={title}
                initialValues={bulkInitialValues}
            />
        );
    },
);

function extractNodeText(node: SerializedLexicalNode): string {
    if ('text' in node && typeof node.text === 'string') {
        return node.text;
    }
    if ('children' in node && Array.isArray(node.children)) {
        return node.children.map(extractNodeText).join('');
    }
    return '';
}

function deriveTaskText(descriptionRich: SerializedEditorState | null): { title: string; detail: string } {
    const paragraphs =
        descriptionRich?.root.children
            .map(extractNodeText)
            .map((line) => line.trim())
            .filter(Boolean) ?? [];

    const [titleLine, ...detailLines] = paragraphs;

    const title = titleLine ?? '';
    const detail = detailLines.join(' · ');

    return { title, detail };
}

export type TaskListItem = {
    key: string;
    values: CrmTaskInputDTO;
    assigneeName?: string;
    pending: boolean;
    onChange: (values: CrmTaskInputDTO) => void;
};

type LogInteractionTaskListProps = {
    customerId: string;
    customerName?: string;
    items: TaskListItem[];
    onAddNewTasks: (tasks: CrmTaskInputDTO[]) => void;
};

export function LogInteractionTaskList({
    customerId,
    customerName,
    items,
    onAddNewTasks,
}: LogInteractionTaskListProps): ReactNode {
    const { t, locale } = useLocale('notes');
    const { enableBulkTaskCreation } = useAppConfig();

    const openCreateTask = (description: string, taskIntent: CrmTaskIntent): void => {
        const title = t('tasks__form_create_title', { ns: 'tasks' });
        const initialValues: Partial<CrmTaskInputDTO> = {
            description: description ? (plainTextToLexicalSync(description) ?? undefined) : undefined,
            taskIntent,
            links: [
                {
                    modelType: SearchEntity.Customer,
                    modelId: customerId,
                    title: customerName ?? null,
                },
            ],
        };

        if (enableBulkTaskCreation) {
            void DialogStateManager.show(TaskBulkDialog, {
                title,
                initialValues,
                onSubmit: onAddNewTasks,
            });
            return;
        }

        void DialogStateManager.show(TaskDialog, {
            title,
            initialValues,
            onSubmit: (task) => {
                onAddNewTasks([task]);
            },
        });
    };

    const openEditTask = (item: TaskListItem): void => {
        void DialogStateManager.show(TaskDialog, {
            title: t('tasks__form_edit_title', { ns: 'tasks' }),
            initialValues: item.values,
            onSubmit: item.onChange,
        });
    };

    return (
        <Accordion type="single" collapsible defaultValue="tasks">
            <AccordionItem value="tasks">
                <AccordionTrigger className="py-3">
                    <Text size="base" weight="semibold" color="neutral-950">
                        {t('notes__tasks_section_title')}
                    </Text>
                </AccordionTrigger>
                <AccordionContent>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="subtle"
                            rounded
                            leftIcon={PlusIcon}
                            className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                            onClick={() => {
                                openCreateTask(t('notes__tasks_quick_action_call'), 'CALL');
                            }}
                        >
                            {t('notes__tasks_quick_action_call')}
                        </Button>
                        <Button
                            type="button"
                            variant="subtle"
                            rounded
                            leftIcon={PlusIcon}
                            className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                            onClick={() => {
                                openCreateTask(t('notes__tasks_quick_action_clarify_price'), 'CLARIFY_PRICE');
                            }}
                        >
                            {t('notes__tasks_quick_action_clarify_price')}
                        </Button>
                        <Button
                            type="button"
                            variant="subtle"
                            rounded
                            leftIcon={PlusIcon}
                            className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                            onClick={() => {
                                openCreateTask('', 'GENERIC');
                            }}
                        >
                            {t('notes__tasks_quick_action_task')}
                        </Button>
                    </div>
                    <div className="flex flex-col gap-2 pt-2">
                        {items.map((item) => {
                            const { title, detail } = deriveTaskText(item.values.description);
                            const dueLabel = formatMonthShortDay(item.values.dueDate, locale);
                            const completed = item.values.status === CrmTaskStatus.COMPLETED;

                            return (
                                <Card key={item.key} className={item.pending ? 'border-dashed' : undefined}>
                                    <CardFront className="card-completed:no-underline!">
                                        <CardComplete
                                            variant="primary"
                                            checked={completed}
                                            onChange={(event) => {
                                                item.onChange({
                                                    ...item.values,
                                                    status: event.target.checked
                                                        ? CrmTaskStatus.COMPLETED
                                                        : CrmTaskStatus.OPEN,
                                                });
                                            }}
                                        />
                                        <CardContent>
                                            <CardHeader className="flex-nowrap items-start">
                                                <CardTitle
                                                    className={`line-clamp-2 min-w-0 flex-1${completed ? ' line-through' : ''}`}
                                                >
                                                    {title}
                                                </CardTitle>
                                                <CardSubtitle className="shrink-0 whitespace-nowrap">
                                                    {item.assigneeName && (
                                                        <>
                                                            <UserIcon className="size-4" />{' '}
                                                            {item.assigneeName}
                                                        </>
                                                    )}
                                                    {dueLabel && (
                                                        <>
                                                            {item.assigneeName && ' · '}
                                                            {dueLabel}
                                                        </>
                                                    )}
                                                </CardSubtitle>
                                            </CardHeader>
                                            {detail && !completed && (
                                                <CardDetail className="line-clamp-3 text-content-weak">
                                                    {detail}
                                                </CardDetail>
                                            )}
                                        </CardContent>
                                        <CardMainAction
                                            onClick={() => {
                                                openEditTask(item);
                                            }}
                                        >
                                            <CardOpenIcon />
                                        </CardMainAction>
                                    </CardFront>
                                </Card>
                            );
                        })}
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
